import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Trash2, Bot } from "lucide-react";

export default function Chat() {
  const t = useT();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => { try { const { data } = await api.get("/coach/assistant/chat"); setMessages(data.messages || []); } catch { /* noop */ } };
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text, created_at: new Date().toISOString() }]);
    setSending(true);
    try { const { data } = await api.post("/coach/assistant/chat", { message: text }); setMessages((m) => [...m, { role: "assistant", content: data.reply, created_at: new Date().toISOString() }]); }
    catch (err) { toast.error(apiError(err)); } finally { setSending(false); }
  };
  const clear = async () => { try { await api.delete("/coach/assistant/chat"); setMessages([]); } catch (err) { toast.error(apiError(err)); } };

  return (
    <div className="flex h-[70vh] flex-col rounded-2xl border border-border bg-card" data-testid="aipa-chat">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <p className="text-sm font-semibold text-foreground">{t("aipa.chat_title")}</p>
        {messages.length > 0 && <button onClick={clear} data-testid="aipa-chat-clear" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /> {t("aipa.chat_clear")}</button>}
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 && !sending && (
          <div className="flex h-full flex-col items-center justify-center text-center" data-testid="aipa-chat-empty"><Bot className="h-8 w-8 text-primary" /><p className="mt-2 text-sm text-muted-foreground">{t("aipa.chat_empty")}</p></div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>{m.content}</div>
          </div>
        ))}
        {sending && <div className="flex justify-start"><div className="rounded-2xl bg-secondary px-3.5 py-2 text-sm text-muted-foreground">{t("aipa.chat_thinking")}</div></div>}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 border-t border-border p-3">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("aipa.chat_ph")} data-testid="aipa-chat-input" />
        <Button className="gap-1.5 rounded-full" disabled={sending} onClick={send} data-testid="aipa-chat-send"><Send className="h-4 w-4" /> {t("aipa.chat_send")}</Button>
      </div>
    </div>
  );
}
